import { DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { QuickSearchResults } from 'src/app/shared/entities/QuickSearchResults';
import { aceDates } from 'src/app/shared/helpers/transform/aceDates';
import { IUpdateAgingCommentsOnInvoice } from 'src/app/shared/interfaces';

@Component({
  selector: 'app-aging-comments',
  templateUrl: './aging-comments.component.html',
  styleUrl: './aging-comments.component.scss',
})
export class AgingCommentsComponent implements OnInit {
  invoiceDetail: QuickSearchResults;
  displayDateString: string = '';
  displayLastUpdatedUserName: string = '';

  // Init Reactive Form
  commentsForm = new FormGroup({
    comments: new FormControl('', [Validators.maxLength(2000)]),
  });

  @Input()
  set idetail(detail: QuickSearchResults) {
    this.invoiceDetail = detail;

    //Becasue we are using a setter, this will update everytime the value is changed in parent component
    // Update the form to reflect new values on new setter update.
    if (this.invoiceDetail != undefined && this.invoiceDetail != null) {
      if (this.invoiceDetail.comments != null && this.invoiceDetail.comments != undefined) {
        this.commentsForm.patchValue({
          comments: this.invoiceDetail.comments,
        });
      }
    }

    this.setDisplayDateString(); //this formats how the date is displayed on form
    this.setLastUpdatedByString(); // this formats how the user name is displayed on form
  }

  //Services
  _dataService = inject(DataService);
  _destroyRefService = inject(DestroyRef);
  _messageDialog = inject(MessageDialogService);
  _appGeneralState = inject(AppGeneralStateService);

  ngOnInit(): void {}

  // Correctly formats/handles null or undefined comment last update date, transforms to local date time and sets display variable
  setDisplayDateString(): void {
    let datePipe: DatePipe = new DatePipe('en-US');
    let aceDate = new aceDates();

    if (this.invoiceDetail != undefined && this.invoiceDetail != null) {
      if (this.invoiceDetail.commentDate.toString() == '0001-01-01T00:00:00+00:00') {
        this.displayDateString = 'N/A';
      } else {
        // use aceDate class :: transform to local typescript date object
        const localizedDate: Date = aceDate.gmtOffsetToLocalDate(this.invoiceDetail.commentDate);

        this.displayDateString = datePipe.transform(localizedDate, 'short');
      }
    }
  }

  // Correctly formats/handles null or undefined commnetUserName and sets display variable
  setLastUpdatedByString(): void {
    if (this.invoiceDetail != undefined && this.invoiceDetail != null) {
      if (
        this.invoiceDetail.commentUserName.toString() == '' ||
        this.invoiceDetail.commentUserName.toString() == undefined ||
        this.invoiceDetail.commentUserName.toString() == null
      ) {
        this.displayLastUpdatedUserName = 'N/A';
      } else {
        this.displayLastUpdatedUserName = this.invoiceDetail.commentUserName;
      }
    }
  }

  // This method is called from the html form to update the db entered by the user
  onUpdateAgingComments(): void {
    let invoiceUpdate: IUpdateAgingCommentsOnInvoice = {} as IUpdateAgingCommentsOnInvoice;
    invoiceUpdate.invoiceNumber = this.invoiceDetail.invoiceNumber;
    invoiceUpdate.comments = this.commentsForm.get('comments').value;
    invoiceUpdate.userName = this._appGeneralState.userName;
    this.updateComments(invoiceUpdate, false); //false = don't clear out values after update
  }

  // This method is called from the html form to clear out any comments and update the db
  onClearExistingComments(): void {
    let invoiceUpdate: IUpdateAgingCommentsOnInvoice = {} as IUpdateAgingCommentsOnInvoice;
    invoiceUpdate.invoiceNumber = this.invoiceDetail.invoiceNumber;
    invoiceUpdate.comments = '';
    invoiceUpdate.userName = this._appGeneralState.userName;
    this.updateComments(invoiceUpdate, true); // true = clear our values on formcontrol after update
  }

  //Call to db to update comments
  //If clearComments = true and call is successful, then remove the comments in form
  updateComments(commentData: IUpdateAgingCommentsOnInvoice, clearComments: boolean) {
    this._dataService
      .updateInvoiceAgingComments(commentData)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {},
        error: (err: any) => {
          this._messageDialog.snackBarTopCenterTimed(
            'An unexpected error occurred and the comments were not updated.  Please try again',
            5000,
          );
        },
        complete: () => {
          // if the clear comments variable is true, then clear out the formControl Comment Value
          if (clearComments) {
            this.commentsForm.patchValue({
              comments: '',
            });
          }

          let datePipe: DatePipe = new DatePipe('en-US');

          this._messageDialog.snackBarTopCenterTimed('The comments have been successfully updated', 3000);
          this.displayDateString = datePipe.transform(new Date(), 'short'); // Update display to reflect what user just updated
          this.displayLastUpdatedUserName = commentData.userName; //Update display to reflect what user just updated
        },
      });
  }
}
